import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  participantsApi,
  type Participant,
  type ParticipantTask,
  type ParticipantUser,
} from "../api/participants";
import { qk } from "../../../../lib/queryKeys";
import { ROUTES } from "../../../../shared/utils";
import { useClientScope } from "../../../../shared/contexts";

const STATUS_OPTIONS = ["Active", "Pending", "On hold", "Completed"];
const FLAGGED_OPTIONS = ["None", "Needs follow-up", "Escalated"];
const GENDER_OPTIONS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const DEFAULT_COLOUR_TAG = "#64748b";

const emptyParticipant: Participant = {
  id: "",
  user_id: "",
  title: "",
  first_name: "",
  surname: "",
  gender: "",
  email: "",
  contact_number: "",
  address_line_1: "",
  address_line_2: "",
  address_line_3: "",
  town: "",
  postcode: "",
  company: "",
  job_role: "",
  status: "",
  current_whoop_user: false,
  whoop_received: false,
  whoop_unite_email_sent: false,
  whoop_unite_connected: false,
  peak4_app: false,
  peak4_api_connected: false,
  date_started: "",
  date_added_to_lms: "",
  date_to_be_removed_from_lms: "",
  color_tag: "",
  flagged: "",
  compliance_asset_id: "",
  notes: "",
  using_own_device: false,
};

const toDateInputValue = (value?: string | null) => {
  if (!value) return "";
  return value.slice(0, 10);
};

const parseDateValue = (value?: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const resolveColourTag = (value?: string | null) => {
  if (!value) return DEFAULT_COLOUR_TAG;
  const normalised = value.trim();
  return /^#[0-9a-fA-F]{6}$/.test(normalised)
    ? normalised
    : DEFAULT_COLOUR_TAG;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

export const ParticipantDetail: React.FC = () => {
  const { participantId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = participantId === "new";
  const { effectiveClientId, isCentralAdmin } = useClientScope();

  const [formState, setFormState] = useState<Participant>(emptyParticipant);
  const [deviceIds, setDeviceIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState("interactions");
  const [interactionForm, setInteractionForm] = useState({
    occurred_at: "",
    type: "",
    summary: "",
    body: "",
  });
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    due_date: "",
  });
  const [taskRows, setTaskRows] = useState<ParticipantTask[]>([]);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const participantQuery = useQuery({
    queryKey: qk.participantDetail(participantId ?? null),
    queryFn: () => participantsApi.get(participantId as string),
    enabled: Boolean(participantId && !isNew),
  });

  const devicesQuery = useQuery({
    queryKey: qk.devices(),
    queryFn: () => participantsApi.listDevices(true),
  });

  const usersQuery = useQuery({
    queryKey: qk.participantUsers(isCentralAdmin ? effectiveClientId ?? null : null),
    queryFn: () =>
      participantsApi.listUsers(isCentralAdmin ? effectiveClientId ?? null : null),
  });

  const interactionsQuery = useQuery({
    queryKey: qk.participantInteractions(participantId ?? null),
    queryFn: () => participantsApi.listInteractions(participantId as string),
    enabled: Boolean(participantId && !isNew),
  });

  const documentsQuery = useQuery({
    queryKey: qk.participantDocuments(participantId ?? null),
    queryFn: () => participantsApi.listDocuments(participantId as string),
    enabled: Boolean(participantId && !isNew),
  });

  const tasksQuery = useQuery({
    queryKey: qk.participantTasks(participantId ?? null),
    queryFn: () => participantsApi.listTasks(participantId as string),
    enabled: Boolean(participantId && !isNew),
  });

  useEffect(() => {
    if (!participantQuery.data?.data) return;
    const { devices, ...participant } = participantQuery.data.data;
    setFormState({
      ...participant,
      date_started: toDateInputValue(participant.date_started),
      date_added_to_lms: toDateInputValue(participant.date_added_to_lms),
      date_to_be_removed_from_lms: toDateInputValue(
        participant.date_to_be_removed_from_lms
      ),
    });
    setDeviceIds(devices?.map((device) => device.id) ?? []);
  }, [participantQuery.data]);

  useEffect(() => {
    if (!tasksQuery.data?.data) return;
    setTaskRows(tasksQuery.data.data);
  }, [tasksQuery.data]);

  const createMutation = useMutation({
    mutationFn: (payload: Participant) =>
      participantsApi.create({
        ...payload,
        user_id: payload.user_id || null,
        device_ids: deviceIds,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["participants"] });
      navigate(`${ROUTES.PARTICIPANTS}/${response.data.id}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Participant) =>
      participantsApi.update(participantId as string, {
        ...payload,
        user_id: payload.user_id || null,
        device_ids: deviceIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.participantDetail(participantId ?? null),
      });
      queryClient.invalidateQueries({ queryKey: ["participants"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => participantsApi.remove(participantId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["participants"] });
      navigate(ROUTES.PARTICIPANTS);
    },
  });

  const createInteractionMutation = useMutation({
    mutationFn: (payload: typeof interactionForm) =>
      participantsApi.createInteraction(participantId as string, payload),
    onSuccess: () => {
      setInteractionForm({ occurred_at: "", type: "", summary: "", body: "" });
      queryClient.invalidateQueries({
        queryKey: qk.participantInteractions(participantId ?? null),
      });
    },
  });

  const createDocumentMutation = useMutation({
    mutationFn: (payload: {
      file_name: string;
      mime_type: string;
      size_bytes: number;
      storage_key: string;
    }) => participantsApi.createDocument(participantId as string, payload),
    onSuccess: () => {
      setUploadFile(null);
      queryClient.invalidateQueries({
        queryKey: qk.participantDocuments(participantId ?? null),
      });
    },
  });

  const deleteDocumentMutation = useMutation({
    mutationFn: (documentId: string) =>
      participantsApi.deleteDocument(participantId as string, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.participantDocuments(participantId ?? null),
      });
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: (payload: typeof taskForm) =>
      participantsApi.createTask(participantId as string, payload),
    onSuccess: () => {
      setTaskForm({ title: "", description: "", due_date: "" });
      queryClient.invalidateQueries({
        queryKey: qk.participantTasks(participantId ?? null),
      });
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: Partial<ParticipantTask> }) =>
      participantsApi.updateTask(participantId as string, taskId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.participantTasks(participantId ?? null),
      });
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) =>
      participantsApi.deleteTask(participantId as string, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.participantTasks(participantId ?? null),
      });
    },
  });

  const applyDefaultsMutation = useMutation({
    mutationFn: () => participantsApi.applyDefaultTasks(participantId as string),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: qk.participantTasks(participantId ?? null),
      });
    },
  });

  const handleSave = () => {
    if (isNew) {
      createMutation.mutate(formState);
    } else {
      updateMutation.mutate(formState);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !participantId) return;
    setUploadError(null);

    try {
      const uploadMeta = await participantsApi.createDocumentUploadUrl(
        participantId,
        {
          file_name: uploadFile.name,
          mime_type: uploadFile.type || "application/octet-stream",
        }
      );

      const uploadResponse = await fetch(uploadMeta.upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": uploadFile.type || "application/octet-stream",
        },
        body: uploadFile,
      });

      if (!uploadResponse.ok) {
        throw new Error("Upload failed");
      }

      createDocumentMutation.mutate({
        file_name: uploadFile.name,
        mime_type: uploadFile.type || "application/octet-stream",
        size_bytes: uploadFile.size,
        storage_key: uploadMeta.storage_key,
      });
    } catch (error) {
      setUploadError("Failed to upload document.");
    }
  };

  const handleTaskUpdate = (taskId: string, payload: Partial<ParticipantTask>) => {
    const existing =
      taskRows.find((task) => task.id === taskId) ??
      tasksQuery.data?.data?.find((task) => task.id === taskId);
    const mergedPayload = existing ? { ...existing, ...payload } : payload;
    updateTaskMutation.mutate({ taskId, payload: mergedPayload });
  };

  const updateTaskLocal = (taskId: string, updates: Partial<ParticipantTask>) => {
    setTaskRows((prev) =>
      prev.map((task) => (task.id === taskId ? { ...task, ...updates } : task))
    );
  };

  const commitTask = (taskId: string) => {
    const task = taskRows.find((item) => item.id === taskId);
    if (task) handleTaskUpdate(taskId, task);
  };

  const deviceOptions = devicesQuery.data?.data ?? [];
  const userOptions = useMemo(
    () => usersQuery.data?.data ?? [],
    [usersQuery.data]
  );
  const selectedUser = useMemo(() => {
    if (!formState.user_id) return null;
    return userOptions.find((user) => user.id === formState.user_id) ?? null;
  }, [formState.user_id, userOptions]);

  return (
    <Box>
      <Stack spacing={3}>
        <Paper sx={{ p: 3 }} elevation={2}>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
              <Box flex={1}>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  {isNew ? "Create participant" : "Participant overview"}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Keep participant information, devices, and tasks up to date.
                </Typography>
              </Box>
              {!isNew && (
                <Stack direction="row" spacing={1} alignItems="center">
                  {formState.status && <Chip label={formState.status} size="small" />}
                  <Typography variant="body2" color="text.secondary">
                    Updated {formatDateTime(formState.updated_at)}
                  </Typography>
                </Stack>
              )}
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <Button variant="outlined" onClick={() => navigate(ROUTES.PARTICIPANTS)}>
                Back to list
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                Save changes
              </Button>
              {!isNew && (
                <Button
                  variant="text"
                  color="error"
                  onClick={() => {
                    if (window.confirm("Delete this participant?")) {
                      deleteMutation.mutate();
                    }
                  }}
                >
                  Delete
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        {participantQuery.isError && !isNew ? (
          <Alert severity="error">Failed to load participant.</Alert>
        ) : null}

        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              lg: 7
            }}>
            <Stack spacing={3}>
              <Paper sx={{ p: 3 }} elevation={2}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Identity
                  </Typography>
                  {!isNew && (
                    <TextField
                      label="Participant ID"
                      value={formState.id}
                      InputProps={{ readOnly: true }}
                    />
                  )}
                  <Autocomplete
                    options={userOptions}
                    value={selectedUser}
                    onChange={(_, value: ParticipantUser | null) => {
                      setFormState((prev) => ({
                        ...prev,
                        user_id: value?.id ?? "",
                        email: value?.email ?? prev.email,
                        first_name: value?.first_name ?? prev.first_name,
                        surname: value?.last_name ?? prev.surname,
                      }));
                    }}
                    getOptionLabel={(option) =>
                      option
                        ? `${option.first_name ?? ""} ${option.last_name ?? ""} (${option.email})`.trim()
                        : ""
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Linked user" placeholder="Select a user" />
                    )}
                  />
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="Title"
                      value={formState.title || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, title: event.target.value }))
                      }
                      fullWidth
                    />
                    <FormControl fullWidth>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        label="Gender"
                        value={formState.gender || ""}
                        onChange={(event) =>
                          setFormState((prev) => ({
                            ...prev,
                            gender: event.target.value,
                          }))
                        }
                      >
                        <MenuItem value="">Not specified</MenuItem>
                        {GENDER_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="First name"
                      required
                      value={formState.first_name}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          first_name: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                    <TextField
                      label="Surname"
                      required
                      value={formState.surname}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          surname: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Paper sx={{ p: 3 }} elevation={2}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Contact and workplace
                  </Typography>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="Email address"
                      value={formState.email || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, email: event.target.value }))
                      }
                      fullWidth
                    />
                    <TextField
                      label="Contact number"
                      value={formState.contact_number || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          contact_number: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </Stack>
                  <Stack spacing={2}>
                    <TextField
                      label="Address line 1"
                      value={formState.address_line_1 || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          address_line_1: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                    <TextField
                      label="Address line 2"
                      value={formState.address_line_2 || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          address_line_2: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                    <TextField
                      label="Address line 3"
                      value={formState.address_line_3 || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          address_line_3: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      <TextField
                        label="Town"
                        value={formState.town || ""}
                        onChange={(event) =>
                          setFormState((prev) => ({ ...prev, town: event.target.value }))
                        }
                        fullWidth
                      />
                      <TextField
                        label="Postcode"
                        value={formState.postcode || ""}
                        onChange={(event) =>
                          setFormState((prev) => ({ ...prev, postcode: event.target.value }))
                        }
                        fullWidth
                      />
                    </Stack>
                  </Stack>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="Company"
                      value={formState.company || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({ ...prev, company: event.target.value }))
                      }
                      fullWidth
                    />
                    <TextField
                      label="Job role"
                      value={formState.job_role || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          job_role: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                  </Stack>
                  <FormControl>
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={formState.status || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          status: event.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">None</MenuItem>
                      {STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Paper>
            </Stack>
          </Grid>

          <Grid
            size={{
              xs: 12,
              lg: 5
            }}>
            <Stack spacing={3}>
              <Paper sx={{ p: 3 }} elevation={2}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Programme flags
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formState.current_whoop_user)}
                        onChange={(event) =>
                          setFormState((prev) => ({
                            ...prev,
                            current_whoop_user: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="Current WHOOP user"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formState.whoop_received)}
                        onChange={(event) =>
                          setFormState((prev) => ({
                            ...prev,
                            whoop_received: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="WHOOP received"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formState.whoop_unite_email_sent)}
                        onChange={(event) =>
                          setFormState((prev) => ({
                            ...prev,
                            whoop_unite_email_sent: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="WHOOP Unite email sent"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formState.whoop_unite_connected)}
                        onChange={(event) =>
                          setFormState((prev) => ({
                            ...prev,
                            whoop_unite_connected: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="WHOOP Unite connected"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formState.peak4_app)}
                        onChange={(event) =>
                          setFormState((prev) => ({
                            ...prev,
                            peak4_app: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="PEAK4 app set up"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formState.peak4_api_connected)}
                        onChange={(event) =>
                          setFormState((prev) => ({
                            ...prev,
                            peak4_api_connected: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="PEAK4 API connected"
                  />
                </Stack>
              </Paper>

              <Paper sx={{ p: 3 }} elevation={2}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Programme dates
                  </Typography>
                  <DatePicker
                    label="Date started"
                    format="DD/MM/YYYY"
                    value={parseDateValue(formState.date_started)}
                    onChange={(newValue) =>
                      setFormState((prev) => ({
                        ...prev,
                        date_started: newValue?.isValid()
                          ? newValue.format("YYYY-MM-DD")
                          : "",
                      }))
                    }
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DatePicker
                    label="Date added to LMS"
                    format="DD/MM/YYYY"
                    value={parseDateValue(formState.date_added_to_lms)}
                    onChange={(newValue) =>
                      setFormState((prev) => ({
                        ...prev,
                        date_added_to_lms: newValue?.isValid()
                          ? newValue.format("YYYY-MM-DD")
                          : "",
                      }))
                    }
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DatePicker
                    label="Date to be removed from LMS"
                    format="DD/MM/YYYY"
                    value={parseDateValue(formState.date_to_be_removed_from_lms)}
                    onChange={(newValue) =>
                      setFormState((prev) => ({
                        ...prev,
                        date_to_be_removed_from_lms: newValue?.isValid()
                          ? newValue.format("YYYY-MM-DD")
                          : "",
                      }))
                    }
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Stack>
              </Paper>

              <Paper sx={{ p: 3 }} elevation={2}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Tags and devices
                  </Typography>
                  <FormControl>
                    <InputLabel>Flagged</InputLabel>
                    <Select
                      label="Flagged"
                      value={formState.flagged || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          flagged: event.target.value,
                        }))
                      }
                    >
                      <MenuItem value="">None</MenuItem>
                      {FLAGGED_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <TextField
                      label="Colour tag"
                      value={formState.color_tag || ""}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          color_tag: event.target.value,
                        }))
                      }
                      fullWidth
                    />
                    <TextField
                      label="Pick colour"
                      type="color"
                      value={resolveColourTag(formState.color_tag)}
                      onChange={(event) =>
                        setFormState((prev) => ({
                          ...prev,
                          color_tag: event.target.value,
                        }))
                      }
                      sx={{ width: { xs: "100%", md: 140 } }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                  <TextField
                    label="Compliance asset ID"
                    value={formState.compliance_asset_id || ""}
                    onChange={(event) =>
                      setFormState((prev) => ({
                        ...prev,
                        compliance_asset_id: event.target.value,
                      }))
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(formState.using_own_device)}
                        onChange={(event) =>
                          setFormState((prev) => ({
                            ...prev,
                            using_own_device: event.target.checked,
                          }))
                        }
                      />
                    }
                    label="Using own device"
                  />

                  <FormControl fullWidth>
                    <InputLabel>Other devices</InputLabel>
                    <Select
                      label="Other devices"
                      multiple
                      value={deviceIds}
                      onChange={(event) =>
                        setDeviceIds(
                          typeof event.target.value === "string"
                            ? event.target.value.split(",")
                            : event.target.value
                        )
                      }
                      renderValue={(selected) =>
                        deviceOptions
                          .filter((device) => selected.includes(device.id))
                          .map((device) => device.name)
                          .join(", ")
                      }
                    >
                      {deviceOptions.map((device) => (
                        <MenuItem key={device.id} value={device.id}>
                          {device.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Paper>

              <Paper sx={{ p: 3 }} elevation={2}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Notes
                  </Typography>
                  <TextField
                    label="General notes"
                    value={formState.notes || ""}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, notes: event.target.value }))
                    }
                    multiline
                    minRows={4}
                  />
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        {!isNew && (
          <Paper sx={{ p: 3 }} elevation={2}>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label="Interactions" value="interactions" />
              <Tab label="Documents" value="documents" />
              <Tab label="Tasks" value="tasks" />
            </Tabs>

            {activeTab === "interactions" && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Typography variant="h6">Interaction log</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 3
                      }}>
                      <TextField
                        label="Date"
                        type="datetime-local"
                        InputLabelProps={{ shrink: true }}
                        value={interactionForm.occurred_at}
                        onChange={(event) =>
                          setInteractionForm((prev) => ({
                            ...prev,
                            occurred_at: event.target.value,
                          }))
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 3
                      }}>
                      <TextField
                        label="Type"
                        value={interactionForm.type}
                        onChange={(event) =>
                          setInteractionForm((prev) => ({
                            ...prev,
                            type: event.target.value,
                          }))
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6
                      }}>
                      <TextField
                        label="Summary"
                        value={interactionForm.summary}
                        onChange={(event) =>
                          setInteractionForm((prev) => ({
                            ...prev,
                            summary: event.target.value,
                          }))
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        label="Notes"
                        value={interactionForm.body}
                        onChange={(event) =>
                          setInteractionForm((prev) => ({
                            ...prev,
                            body: event.target.value,
                          }))
                        }
                        fullWidth
                        multiline
                        minRows={3}
                      />
                    </Grid>
                    <Grid size={12}>
                      <Button
                        variant="contained"
                        onClick={() => createInteractionMutation.mutate(interactionForm)}
                        disabled={createInteractionMutation.isPending}
                      >
                        Add interaction
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                <Divider />

                {interactionsQuery.isLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading interactions...</Typography>
                  </Box>
                ) : interactionsQuery.isError ? (
                  <Alert severity="error">Failed to load interactions.</Alert>
                ) : interactionsQuery.data?.data?.length ? (
                  <Stack spacing={2}>
                    {interactionsQuery.data.data.map((interaction) => (
                      <Paper key={interaction.id} variant="outlined" sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="subtitle2">
                            {interaction.type || "Interaction"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDateTime(interaction.occurred_at)}
                          </Typography>
                        </Stack>
                        {interaction.summary && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {interaction.summary}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {interaction.body}
                        </Typography>
                        {(interaction.first_name || interaction.last_name) && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                            {interaction.first_name} {interaction.last_name}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">No interactions yet.</Alert>
                )}
              </Stack>
            )}

            {activeTab === "documents" && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Typography variant="h6">Documents</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
                    <Button variant="outlined" component="label">
                      Choose file
                      <input
                        type="file"
                        hidden
                        onChange={(event) =>
                          setUploadFile(event.target.files?.[0] ?? null)
                        }
                      />
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {uploadFile ? uploadFile.name : "No file selected"}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleUpload}
                      disabled={!uploadFile || createDocumentMutation.isPending}
                    >
                      Upload
                    </Button>
                  </Stack>
                  {uploadError && <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>}
                </Paper>

                <Divider />

                {documentsQuery.isLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading documents...</Typography>
                  </Box>
                ) : documentsQuery.isError ? (
                  <Alert severity="error">Failed to load documents.</Alert>
                ) : documentsQuery.data?.data?.length ? (
                  <Stack spacing={2}>
                    {documentsQuery.data.data.map((doc) => (
                      <Paper key={doc.id} variant="outlined" sx={{ p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography variant="subtitle2">{doc.file_name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {doc.size_bytes ? `${Math.round(doc.size_bytes / 1024)} KB` : "-"} · {formatDateTime(doc.uploaded_at)}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            {doc.file_url && (
                              <Button
                                variant="outlined"
                                size="small"
                                component="a"
                                href={doc.file_url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Open
                              </Button>
                            )}
                            <Button
                              variant="text"
                              color="error"
                              size="small"
                              onClick={() => deleteDocumentMutation.mutate(doc.id)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">No documents uploaded.</Alert>
                )}
              </Stack>
            )}

            {activeTab === "tasks" && (
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                  <Box>
                    <Typography variant="h6">Task checklist</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {taskRows.filter((task) => task.status === "done").length} of {taskRows.length} completed
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => applyDefaultsMutation.mutate()}
                    disabled={applyDefaultsMutation.isPending}
                  >
                    Apply default tasks
                  </Button>
                </Stack>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid
                      size={{
                        xs: 12,
                        md: 6
                      }}>
                      <TextField
                        label="Task title"
                        value={taskForm.title}
                        onChange={(event) =>
                          setTaskForm((prev) => ({
                            ...prev,
                            title: event.target.value,
                          }))
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 3
                      }}>
                      <TextField
                        label="Due date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={taskForm.due_date}
                        onChange={(event) =>
                          setTaskForm((prev) => ({
                            ...prev,
                            due_date: event.target.value,
                          }))
                        }
                        fullWidth
                      />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 3
                      }}>
                      <Button
                        variant="contained"
                        onClick={() => createTaskMutation.mutate(taskForm)}
                        disabled={createTaskMutation.isPending}
                      >
                        Add task
                      </Button>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        label="Task notes"
                        value={taskForm.description}
                        onChange={(event) =>
                          setTaskForm((prev) => ({
                            ...prev,
                            description: event.target.value,
                          }))
                        }
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Grid>
                  </Grid>
                </Paper>

                <Divider />

                {tasksQuery.isLoading ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading tasks...</Typography>
                  </Box>
                ) : tasksQuery.isError ? (
                  <Alert severity="error">Failed to load tasks.</Alert>
                ) : taskRows.length ? (
                  <Stack spacing={2}>
                    {taskRows.map((task) => (
                      <Paper key={task.id} variant="outlined" sx={{ p: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid
                            size={{
                              xs: 12,
                              md: 6
                            }}>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Checkbox
                                checked={task.status === "done"}
                                onChange={(event) => {
                                  const nextStatus = event.target.checked ? "done" : "todo";
                                  updateTaskLocal(task.id, { status: nextStatus });
                                  handleTaskUpdate(task.id, { status: nextStatus });
                                }}
                              />
                              <TextField
                                label="Title"
                                value={task.title}
                                onChange={(event) =>
                                  updateTaskLocal(task.id, { title: event.target.value })
                                }
                                onBlur={() => commitTask(task.id)}
                                fullWidth
                              />
                            </Stack>
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              md: 3
                            }}>
                            <TextField
                              label="Due"
                              type="date"
                              InputLabelProps={{ shrink: true }}
                              value={task.due_date ? task.due_date.slice(0, 10) : ""}
                              onChange={(event) =>
                                updateTaskLocal(task.id, { due_date: event.target.value })
                              }
                              onBlur={() => commitTask(task.id)}
                              fullWidth
                            />
                          </Grid>
                          <Grid
                            size={{
                              xs: 12,
                              md: 3
                            }}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Chip label={task.status || "todo"} size="small" />
                              <Button
                                variant="text"
                                color="error"
                                onClick={() => deleteTaskMutation.mutate(task.id)}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </Grid>
                          <Grid size={12}>
                            <TextField
                              label="Description"
                              value={task.description || ""}
                              onChange={(event) =>
                                updateTaskLocal(task.id, { description: event.target.value })
                              }
                              onBlur={() => commitTask(task.id)}
                              fullWidth
                              multiline
                              minRows={2}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Alert severity="info">No tasks created yet.</Alert>
                )}
              </Stack>
            )}
          </Paper>
        )}
      </Stack>
    </Box>
  );
};
